import React, { useState } from 'react';
import { XMarkIcon, CloudArrowUpIcon, DocumentIcon } from '@heroicons/react/24/outline';
import { API_URL } from '../../api/config';

interface MaterialUploadModalProps {
    isOpen: boolean;
    onClose: () => void;
    onUpload: (materialData: any) => void;
}

const MaterialUploadModal: React.FC<MaterialUploadModalProps> = ({ isOpen, onClose, onUpload }) => {
    const [formData, setFormData] = useState({
        title: '',
        category: 'Agronomy',
        fileType: 'PDF',
        description: '',
        language: 'en',
        targetAudience: 'all',
        version: '1.0'
    });

    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    const handleChange = (field: string, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setSelectedFile(file);
            
            // Auto-detect file type
            const ext = file.name.split('.').pop()?.toLowerCase();
            if (['mp4', 'avi', 'mov'].includes(ext || '')) {
                setFormData(prev => ({ ...prev, fileType: 'Video' }));
            } else if (['jpg', 'jpeg', 'png'].includes(ext || '')) {
                setFormData(prev => ({ ...prev, fileType: 'Image' }));
            } else if (['mp3', 'wav'].includes(ext || '')) {
                setFormData(prev => ({ ...prev, fileType: 'Audio' }));
            } else {
                setFormData(prev => ({ ...prev, fileType: 'PDF' }));
            }
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setUploadProgress(0);

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('title', formData.title);
            formDataToSend.append('category', formData.category);
            formDataToSend.append('description', formData.description);
            formDataToSend.append('language', formData.language);
            formDataToSend.append('target_audience', formData.targetAudience);
            formDataToSend.append('version', formData.version);
            
            if (selectedFile) {
                formDataToSend.append('file', selectedFile);
            }

            const token = localStorage.getItem('aaywa_token');
            const response = await fetch(`${API_URL}/api/agronomy/materials/upload`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formDataToSend
            });

            if (!response.ok) {
                throw new Error('Upload failed');
            }

            const result = await response.json();
            setUploadProgress(100);
            
            // Call parent callback
            onUpload(result.material);
            
            // Reset form
            setFormData({
                title: '',
                category: 'Agronomy',
                fileType: 'PDF',
                description: '',
                language: 'en',
                targetAudience: 'all',
                version: '1.0'
            });
            setSelectedFile(null);
            onClose();
        } catch (err) {
            console.error('Upload error:', err);
            alert('Failed to upload material. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full my-8">
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-4 rounded-t-2xl flex justify-between items-center">
                    <div className="flex items-center space-x-2">
                        <div className="bg-white bg-opacity-20 p-1.5 rounded-lg">
                            <CloudArrowUpIcon className="h-5 w-5 text-white" />
                        </div>
                        <h2 className="text-lg font-bold text-white">Upload Material</h2>
                    </div>
                    <button onClick={onClose} className="text-white hover:bg-white/20 p-1.5 rounded-lg">
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-5 space-y-3 max-h-[calc(100vh-12rem)] overflow-y-auto">
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Title *</label>
                        <input
                            type="text"
                            required
                            value={formData.title}
                            onChange={e => handleChange('title', e.target.value)}
                            className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500"
                            placeholder="e.g. Pest Control Guide"
                        />
                    </div>

                    {/* File Upload */}
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Upload File *</label>
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-500 transition-colors">
                            <input
                                type="file"
                                onChange={handleFileSelect}
                                className="hidden"
                                id="file-upload"
                                accept=".pdf,.doc,.docx,.ppt,.pptx,.mp4,.avi,.mov,.jpg,.jpeg,.png,.mp3,.wav"
                            />
                            <label htmlFor="file-upload" className="cursor-pointer">
                                <CloudArrowUpIcon className="h-10 w-10 text-gray-400 mx-auto mb-1" />
                                <p className="text-xs text-gray-600">
                                    {selectedFile ? selectedFile.name : 'Click to upload or drag and drop'}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                    PDF, DOC, PPT, Video, Image, Audio (Max 100MB)
                                </p>
                            </label>
                        </div>
                        {uploadProgress > 0 && uploadProgress < 100 && (
                            <div className="mt-2">
                                <div className="w-full bg-gray-200 rounded-full h-1.5">
                                    <div 
                                        className="bg-blue-600 h-1.5 rounded-full transition-all"
                                        style={{ width: `${uploadProgress}%` }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Version</label>
                            <input
                                type="text"
                                value={formData.version}
                                onChange={e => handleChange('version', e.target.value)}
                                className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500"
                                placeholder="1.0"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
                            <select
                                value={formData.category}
                                onChange={e => handleChange('category', e.target.value)}
                                className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="Agronomy">Agronomy</option>
                                <option value="VSLA">VSLA</option>
                                <option value="Nutrition">Nutrition</option>
                                <option value="Compost">Compost</option>
                                <option value="General">General</option>
                            </select>
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Target Audience</label>
                        <select
                            value={formData.targetAudience}
                            onChange={e => handleChange('targetAudience', e.target.value)}
                            className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="all">All Participants</option>
                            <option value="champions">Champions Only</option>
                            <option value="farmers">Farmers Only</option>
                            <option value="staff">Staff Only</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                        <textarea
                            value={formData.description}
                            onChange={e => handleChange('description', e.target.value)}
                            rows={2}
                            className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500"
                            placeholder="Brief description..."
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Language</label>
                            <select
                                value={formData.language}
                                onChange={e => handleChange('language', e.target.value)}
                                className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="en">English</option>
                                <option value="rw">Kinyarwanda</option>
                                <option value="fr">French</option>
                            </select>
                        </div>
                    </div>

                    <div className="flex justify-end space-x-2 pt-3 border-t">
                        <button 
                            type="button" 
                            onClick={onClose} 
                            className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button 
                            type="submit" 
                            className="px-5 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                            disabled={loading || !selectedFile}
                        >
                            {loading ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    <span>Uploading...</span>
                                </>
                            ) : (
                                <>
                                    <CloudArrowUpIcon className="h-4 w-4" />
                                    <span>Upload</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default MaterialUploadModal;
